/**
 * Household screen — solo, invitation pending, or paired.
 *
 * Two things changed here from the original "Share with partner" screen, both
 * of which were causing the state to read as a lie:
 *
 *  1. The household is created when you tap "Create an invitation", not when
 *     the screen mounts. Mounting used to create a household document and
 *     migrate all your data into it, which flipped Settings to "Household
 *     paired" before anyone had been invited.
 *
 *  2. There is one invitation at a time. The screen used to call createInvite
 *     on mount, so every visit minted a new code and left the previous ones
 *     valid for their full 24 hours — 13 of them accumulated on one account.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ActivityIndicator,
  ScrollView,
  Share,
  Platform,
  TextInput,
  Modal,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import * as Clipboard from 'expo-clipboard';
import Feather from '@expo/vector-icons/Feather';
import {
  Paprika,
  PaprikaTypography,
  PaprikaSpacing,
  PaprikaRadius,
  PaprikaShadows,
} from '../src/styles/theme';
import { RootState, AppDispatch } from '../src/redux/store';
import { setHouseholdId } from '../src/redux/authSlice';
import {
  ensureInvite,
  revokeInvite,
  buildInviteLink,
  setDisplayName,
  publishMemberProfile,
  countHouseholdContents,
  HouseholdError,
  type HouseholdContents,
} from '../src/services/householdService';
import { householdErrorKey, type HouseholdAction } from '../src/services/householdErrorCopy';
import { trackEvent, captureError } from '../src/services/analyticsService';
import { useHouseholdExit } from '../src/hooks/useHouseholdExit';
import { useHouseholdMembers } from '../src/hooks/useHouseholdMembers';
import { EatersSection } from '../src/components/household/EatersSection';
import { auth } from '../src/services/firebaseConfig';

/**
 * Every failed household action is counted, with its reason. A reason we have
 * no sentence for is also an exception, because it is a fault rather than a
 * state the cook can act on: #776 reached us as a screenshot, not an event.
 */
const reportHouseholdFailure = (action: HouseholdAction, err: unknown): void => {
  const reason = err instanceof HouseholdError ? err.code : 'unknown';
  trackEvent('household_action_failed', { action, reason });
  if (reason === 'unknown' || reason === 'not-authorized') {
    captureError(err instanceof Error ? err : new Error(String(err)), {
      source: 'share-partner',
      action,
    });
  }
};

/** Whole hours left, floored, never negative. */
const hoursUntil = (millis: number): number =>
  Math.max(0, Math.floor((millis - Date.now()) / 3_600_000));

type ExitKind = 'leave' | 'remove';
type ExitPhase = 'confirm' | 'working';

/**
 * What to tell the user once the sheet has closed.
 *
 * `left-unverified` is the one that earns its keep: the server reported it
 * copied the data back, the re-pull found none of it, and saying so beats
 * dropping somebody into an app that looks wiped.
 */
type ExitOutcomeBanner =
  | { kind: 'left'; recipes: number; ratings: number; pantry: number }
  | { kind: 'left-unverified' }
  | { kind: 'removed'; name: string };

const SHARED_SURFACES = [
  { icon: 'calendar', key: 'sharesPlan' },
  { icon: 'shopping-cart', key: 'sharesGrocery' },
  { icon: 'book-open', key: 'sharesRecipes' },
  { icon: 'package', key: 'sharesPantry' },
] as const;

export default function SharePartnerScreen() {
  const { t } = useTranslation();
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();
  const { user, householdId } = useSelector((state: RootState) => state.auth);
  const household = useHouseholdMembers();

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [nameDraft, setNameDraft] = useState('');
  const [nameAsked, setNameAsked] = useState(false);

  // The exit (#604). `exitKind` is which sheet is open, `exitPhase` is how far
  // it has got, and `outcome` is what to say once it has closed.
  const [exitKind, setExitKind] = useState<ExitKind | null>(null);
  const [exitPhase, setExitPhase] = useState<ExitPhase>('confirm');
  const [contents, setContents] = useState<HouseholdContents | null>(null);
  const [outcome, setOutcome] = useState<ExitOutcomeBanner | null>(null);
  const { leave, remove } = useHouseholdExit();

  // Firebase only fills displayName for Google sign-in, so an email/password
  // account would show its address to the partner forever. Ask once, at the
  // moment the name is about to become visible to someone else.
  const needsName = !nameAsked && !auth?.currentUser?.displayName;

  const invite = household.pendingInvite;
  const expiryHours = useMemo(
    () => (invite ? hoursUntil(invite.expiresAt.toMillis()) : 0),
    [invite]
  );

  // Publish my identity into the roster, so the partner sees a name rather
  // than a uid — once the household has been read, and only if it differs.
  //
  // Never before the first read (#776). This used to fire the moment the id
  // appeared, a blind `updateDoc` on a document the SDK had never seen, and an
  // acknowledged write on an unread document makes the web SDK discard the
  // server's next, slightly older read of it: for that moment the household
  // "does not exist" to every reader on this device.
  const myRosterEntry = household.myUid ? household.roster?.[household.myUid] : undefined;
  const householdRead = !household.loading && !!householdId && household.householdId === householdId;
  useEffect(() => {
    const me = auth?.currentUser;
    if (!householdRead || !householdId || !me) return;
    const displayName = me.displayName ?? null;
    const email = me.email ?? null;
    if (myRosterEntry?.displayName === displayName && (myRosterEntry?.email ?? null) === email) return;
    publishMemberProfile(householdId, me.uid, displayName, email).catch(() => {});
  }, [householdRead, householdId, myRosterEntry?.displayName, myRosterEntry?.email]);

  const saveName = useCallback(async () => {
    const trimmed = nameDraft.trim();
    setNameAsked(true);
    if (!trimmed) return;
    await setDisplayName(trimmed, householdId).catch(() => {});
  }, [nameDraft, householdId]);

  const handleCreateInvitation = useCallback(async () => {
    if (!user?.uid) {
      setError(t('household.errSignedOut'));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      if (needsName) await saveName();

      // One request (#776): the server creates the household if there is none
      // — the first moment the user has actually asked to share something,
      // with their data copied in before they become a member — then returns
      // its live invitation. Nothing is read back here, and the household is
      // the one the server found on the profile, not this screen's copy of
      // the id, which a profile read still in flight from sign-in can reset.
      const { householdId: activeHouseholdId } = await ensureInvite(user.uid);
      if (activeHouseholdId !== householdId) {
        dispatch(setHouseholdId(activeHouseholdId));
      }
    } catch (err) {
      reportHouseholdFailure('invite', err);
      setError(t(householdErrorKey(err, 'invite')));
    } finally {
      setBusy(false);
    }
  }, [user?.uid, householdId, dispatch, t, needsName, saveName]);

  const handleCancelInvitation = useCallback(async () => {
    if (!householdId || !user?.uid) return;
    setBusy(true);
    setError(null);
    try {
      await revokeInvite(user.uid);
    } catch (err) {
      reportHouseholdFailure('revoke', err);
      setError(t(householdErrorKey(err, 'revoke')));
    } finally {
      setBusy(false);
    }
  }, [householdId, user?.uid, t]);

  const openExit = useCallback(
    (kind: ExitKind) => {
      setExitKind(kind);
      setExitPhase('confirm');
      setError(null);
      setContents(null);
      // Counted live rather than read off Redux: the store holds what this
      // device happens to have loaded, and the sheet is about to make a
      // promise about somebody's whole library.
      if (householdId) {
        countHouseholdContents(householdId).then(setContents).catch(() => {});
      }
    },
    [householdId]
  );

  // Deliberately refuses while the copy is running. It is already underway on
  // the server, and a half-finished exit is the one state worth never offering.
  const closeExit = useCallback(() => {
    setExitPhase((phase) => {
      if (phase !== 'working') setExitKind(null);
      return phase;
    });
  }, []);

  const confirmExit = useCallback(async () => {
    if (!exitKind) return;
    setExitPhase('working');
    setError(null);
    try {
      if (exitKind === 'leave') {
        const result = await leave();
        setOutcome(
          result.verified
            ? {
                kind: 'left',
                recipes: result.restored.savedRecipes ?? 0,
                ratings: result.restored.recipeMetadata ?? 0,
                pantry: result.restored.inventory ?? 0,
              }
            : { kind: 'left-unverified' }
        );
      } else {
        if (!household.partnerUid) return;
        // Captured before the call: the roster entry is gone by the time it
        // returns, so reading the name afterwards would render "?".
        const name = household.partnerName ?? '';
        await remove(household.partnerUid);
        setOutcome({ kind: 'removed', name });
      }
      setExitKind(null);
    } catch (err) {
      reportHouseholdFailure('leave', err);
      setError(t(householdErrorKey(err, 'leave')));
      setExitPhase('confirm');
    }
  }, [exitKind, leave, remove, household.partnerUid, household.partnerName, t]);

  const handleShare = useCallback(async () => {
    if (!invite) return;
    const message = t('household.shareMessage', {
      link: buildInviteLink(invite.code),
      code: invite.code,
    });
    try {
      if (Platform.OS === 'web' && (navigator as any).share) {
        await (navigator as any).share({ text: message });
      } else {
        await Share.share({ message });
      }
    } catch {
      // unreported: the share sheet rejects with AbortError when the cook
      // closes it, and that is the only way it fails.
    }
  }, [invite, t]);

  const handleCopy = useCallback(async () => {
    if (!invite) return;
    await Clipboard.setStringAsync(invite.code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [invite]);

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity onPress={() => router.back()} style={styles.backBtn}>
          <Feather name="arrow-left" size={24} color={Paprika.textPrimary} />
        </TouchableOpacity>
        <Text style={styles.title}>{t('household.headerTitle')}</Text>
      </View>

      <ScrollView contentContainerStyle={styles.body}>
        {outcome && (
          <OutcomeBanner t={t} outcome={outcome} onDismiss={() => setOutcome(null)} />
        )}

        {household.status === 'paired' ? (
          <PairedView
            t={t}
            partnerName={household.partnerName}
            partnerEmail={household.partnerEmail}
            partnerInitial={household.partnerInitial}
            myName={household.myName}
            myInitial={household.myInitial}
            onLeave={() => openExit('leave')}
            onRemove={household.partnerUid ? () => openExit('remove') : undefined}
          />
        ) : household.status === 'pending' && invite ? (
          <>
            <View style={styles.card}>
              <Text style={styles.cardHeadline}>{t('household.pendingHeadline')}</Text>
              <Text style={styles.cardBody}>{t('household.pendingBody')}</Text>
            </View>

            <Text style={styles.codeLabel}>{t('household.codeLabel')}</Text>
            <View style={styles.codeBox}>
              <Text style={styles.codeText}>{invite.code}</Text>
              <Text style={styles.codeExpiry}>
                {expiryHours > 0
                  ? t('household.expiresIn', { hours: expiryHours })
                  : t('household.expiresSoon')}
              </Text>
            </View>

            <TouchableOpacity style={styles.primaryBtn} onPress={handleShare}>
              <Feather name="share-2" size={18} color={Paprika.onPrimary} />
              <Text style={styles.primaryBtnText}>{t('household.shareLink')}</Text>
            </TouchableOpacity>

            <TouchableOpacity style={styles.secondaryBtn} onPress={handleCopy}>
              <Feather
                name={copied ? 'check' : 'copy'}
                size={18}
                color={Paprika.textPrimary}
              />
              <Text style={styles.secondaryBtnText}>
                {copied ? t('household.copied') : t('household.copyCode')}
              </Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={styles.destructiveBtn}
              onPress={handleCancelInvitation}
              disabled={busy}
            >
              <Text style={styles.destructiveBtnText}>
                {busy ? t('household.cancelling') : t('household.cancelInvitation')}
              </Text>
            </TouchableOpacity>
          </>
        ) : (
          <>
            <View style={styles.card}>
              <Feather name="users" size={36} color={Paprika.accent} />
              <Text style={styles.heroHeadline}>{t('household.soloHeadline')}</Text>
              <Text style={styles.cardBody}>{t('household.soloBody')}</Text>

              <View style={styles.sharesList}>
                {SHARED_SURFACES.map(({ icon, key }) => (
                  <View key={key} style={styles.shareRow}>
                    <Feather name={icon} size={15} color={Paprika.textMuted} />
                    <Text style={styles.shareRowText}>{t(`household.${key}`)}</Text>
                  </View>
                ))}
              </View>
            </View>

            {needsName && (
              <View style={styles.nameCard}>
                <Text style={styles.nameTitle}>{t('household.nameTitle')}</Text>
                <Text style={styles.cardBody}>{t('household.nameBody')}</Text>
                <TextInput
                  style={styles.nameInput}
                  value={nameDraft}
                  onChangeText={setNameDraft}
                  placeholder={t('household.namePlaceholder')}
                  placeholderTextColor={Paprika.textMuted}
                  autoCapitalize="words"
                  returnKeyType="done"
                />
              </View>
            )}

            <TouchableOpacity
              style={[styles.primaryBtn, busy && styles.btnDisabled]}
              onPress={handleCreateInvitation}
              disabled={busy}
            >
              {busy ? (
                <ActivityIndicator size="small" color={Paprika.onPrimary} />
              ) : (
                <Feather name="plus" size={18} color={Paprika.onPrimary} />
              )}
              <Text style={styles.primaryBtnText}>
                {busy ? t('household.creating') : t('household.createInvitation')}
              </Text>
            </TouchableOpacity>
          </>
        )}

        {/* Who eats here — the table, not just the two accounts. Shown in
            every pairing state: a solo parent still has children to name. */}
        <EatersSection />

        {!!error && (
          <View style={styles.errorCard}>
            <Text style={styles.errorText}>{error}</Text>
            <TouchableOpacity onPress={() => setError(null)}>
              <Text style={styles.retryText}>{t('household.tryAgain')}</Text>
            </TouchableOpacity>
          </View>
        )}
      </ScrollView>

      {exitKind && (
        <ExitSheet
          t={t}
          kind={exitKind}
          phase={exitPhase}
          partnerName={household.partnerName}
          contents={contents}
          onConfirm={confirmExit}
          onCancel={closeExit}
        />
      )}
    </View>
  );
}

const MemberRow = ({
  initial,
  name,
  email,
  tag,
  isSelf,
}: {
  initial: string | null;
  name: string | null;
  email: string | null;
  tag: string;
  isSelf: boolean;
}) => (
  <View style={styles.memberRow}>
    <View style={[styles.avatar, isSelf && styles.avatarSelf]}>
      <Text style={[styles.avatarText, isSelf && styles.avatarTextSelf]}>
        {initial ?? '?'}
      </Text>
    </View>
    <View style={styles.memberText}>
      <Text style={styles.memberName}>{name ?? email ?? '—'}</Text>
      {name && email ? <Text style={styles.memberEmail}>{email}</Text> : null}
    </View>
    <Text style={styles.memberTag}>{tag}</Text>
  </View>
);

const PairedView = ({
  t,
  partnerName,
  partnerEmail,
  partnerInitial,
  myName,
  myInitial,
  onLeave,
  onRemove,
}: {
  t: (k: string, o?: Record<string, unknown>) => string;
  partnerName: string | null;
  partnerEmail: string | null;
  partnerInitial: string | null;
  myName: string | null;
  myInitial: string | null;
  onLeave: () => void;
  /** Absent until the partner's uid is known — there is nobody to remove. */
  onRemove?: () => void;
}) => {
  const me = auth?.currentUser;
  return (
    <>
      <View style={styles.card}>
        <Text style={styles.cardHeadline}>{t('household.pairedHeadline')}</Text>
        <Text style={styles.cardBody}>{t('household.pairedBody')}</Text>
      </View>

      <View style={styles.roster}>
        <MemberRow
          initial={myInitial ?? (me?.displayName ?? me?.email ?? '?').charAt(0).toUpperCase()}
          name={myName ?? me?.displayName ?? null}
          email={me?.email ?? null}
          tag={t('household.you')}
          isSelf
        />
        <MemberRow
          initial={partnerInitial}
          name={partnerName}
          email={partnerEmail}
          tag=""
          isSelf={false}
        />
      </View>

      <View style={styles.sharesList}>
        {SHARED_SURFACES.map(({ icon, key }) => (
          <View key={key} style={styles.shareRow}>
            <Feather name={icon} size={15} color={Paprika.textMuted} />
            <Text style={styles.shareRowText}>{t(`household.${key}`)}</Text>
          </View>
        ))}
      </View>

      {/* Two exits, weighted differently. Leaving is yours to do, so it reads
          in terracotta; removing somebody else is quieter and sits below. */}
      <View style={styles.exitActions}>
        <TouchableOpacity style={[styles.exitRow, styles.exitRowFirst]} onPress={onLeave}>
          <Feather name="log-out" size={18} color={Paprika.accent} />
          <Text style={styles.exitRowTextLeave}>{t('household.leaveHousehold')}</Text>
        </TouchableOpacity>
        {onRemove && (
          <TouchableOpacity style={[styles.exitRow, styles.exitRowLast]} onPress={onRemove}>
            <Feather name="user-minus" size={18} color={Paprika.textSecondary} />
            <Text style={styles.exitRowText}>
              {t('household.removePartner', { name: partnerName ?? '' })}
            </Text>
          </TouchableOpacity>
        )}
      </View>
    </>
  );
};

/**
 * The confirmation, and the progress that follows it.
 *
 * One sheet with two phases rather than two components: the transition from
 * "are you sure" to "moving your things" should read as the same surface
 * carrying on, not as one modal replacing another.
 */
const ExitSheet = ({
  t,
  kind,
  phase,
  partnerName,
  contents,
  onConfirm,
  onCancel,
}: {
  t: (k: string, o?: Record<string, unknown>) => string;
  kind: ExitKind;
  phase: ExitPhase;
  partnerName: string | null;
  contents: HouseholdContents | null;
  onConfirm: () => void;
  onCancel: () => void;
}) => {
  const leaving = kind === 'leave';
  const working = phase === 'working';

  // Only the surfaces that actually hold something. A household with no pantry
  // should not be told it keeps zero pantry items.
  const lines = contents
    ? ([
        ['keepRecipes', contents.savedRecipes],
        ['keepRatings', contents.recipeMetadata],
        ['keepPantry', contents.inventory],
        ['keepWeeks', contents.mealPlan],
      ] as const).filter(([, n]) => n > 0)
    : [];

  return (
    <Modal visible transparent animationType="slide" onRequestClose={onCancel}>
      <View style={styles.scrim}>
        <View style={styles.sheet}>
          <View style={styles.grabber} />

          {working ? (
            <>
              <Text style={styles.sheetTitle}>
                {t(leaving ? 'household.leavingTitle' : 'household.removingTitle')}
              </Text>
              <Text style={styles.sheetBody}>
                {t(leaving ? 'household.leavingBody' : 'household.removingBody')}
              </Text>
              <View style={[styles.primaryBtn, styles.btnDisabled, styles.sheetBtn]}>
                <ActivityIndicator size="small" color={Paprika.onPrimary} />
                <Text style={styles.primaryBtnText}>
                  {t(leaving ? 'household.leaving' : 'household.removing')}
                </Text>
              </View>
            </>
          ) : (
            <>
              <Text style={styles.sheetTitle}>
                {leaving
                  ? t('household.leaveTitle')
                  : t('household.removeTitle', { name: partnerName ?? '' })}
              </Text>
              <Text style={styles.sheetBody}>
                {t(leaving ? 'household.leaveBody' : 'household.removeBody')}
              </Text>

              {lines.length > 0 && (
                <View style={styles.keeps}>
                  <Text style={styles.keepsHead}>
                    {t(leaving ? 'household.keepsHead' : 'household.keepsHeadThem')}
                  </Text>
                  {lines.map(([key, n]) => (
                    <View key={key} style={styles.keepsLine}>
                      <Text style={styles.keepsLabel}>{t(`household.${key}`)}</Text>
                      <Text style={styles.keepsCount}>{n}</Text>
                    </View>
                  ))}
                </View>
              )}

              <TouchableOpacity
                style={[styles.primaryBtn, styles.sheetBtn]}
                onPress={onConfirm}
              >
                <Text style={styles.primaryBtnText}>
                  {t(leaving ? 'household.leaveConfirm' : 'household.removeConfirm')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity style={styles.ghostBtn} onPress={onCancel}>
                <Text style={styles.ghostBtnText}>{t('household.stay')}</Text>
              </TouchableOpacity>
            </>
          )}
        </View>
      </View>
    </Modal>
  );
};

/** What happened, once the sheet has gone. */
const OutcomeBanner = ({
  t,
  outcome,
  onDismiss,
}: {
  t: (k: string, o?: Record<string, unknown>) => string;
  outcome: ExitOutcomeBanner;
  onDismiss: () => void;
}) => {
  if (outcome.kind === 'left-unverified') {
    return (
      <View style={styles.bannerWarn}>
        <Text style={styles.bannerWarnTitle}>
          {t('household.leftUnverifiedTitle')}
        </Text>
        <Text style={styles.bannerWarnBody}>{t('household.leftUnverifiedBody')}</Text>
        {Platform.OS === 'web' && (
          <TouchableOpacity
            style={styles.reloadBtn}
            onPress={() => window.location.reload()}
          >
            <Text style={styles.reloadBtnText}>{t('household.reload')}</Text>
          </TouchableOpacity>
        )}
      </View>
    );
  }

  return (
    <TouchableOpacity style={styles.bannerOk} onPress={onDismiss} activeOpacity={0.8}>
      <Text style={styles.bannerOkTitle}>
        {outcome.kind === 'left'
          ? t('household.leftTitle')
          : t('household.removedTitle', { name: outcome.name })}
      </Text>
      <Text style={styles.bannerOkBody}>
        {outcome.kind === 'left'
          ? t('household.leftBody', {
              recipes: outcome.recipes,
              ratings: outcome.ratings,
              pantry: outcome.pantry,
            })
          : t('household.removedBody')}
      </Text>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  container: { flex: 1, backgroundColor: Paprika.background },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: PaprikaSpacing.lg,
    paddingTop: Platform.OS === 'web' ? PaprikaSpacing.lg : PaprikaSpacing.xxl,
    paddingBottom: PaprikaSpacing.md,
  },
  backBtn: { padding: PaprikaSpacing.xs, marginRight: PaprikaSpacing.sm },
  title: { ...PaprikaTypography.headingMedium, color: Paprika.textPrimary },
  body: { padding: PaprikaSpacing.md, paddingBottom: PaprikaSpacing.xxl },

  card: {
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: PaprikaRadius.lg,
    padding: PaprikaSpacing.lg,
    alignItems: 'center',
    ...PaprikaShadows.card,
  },
  heroHeadline: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    marginTop: PaprikaSpacing.md,
    marginBottom: PaprikaSpacing.xs,
    textAlign: 'center',
  },
  cardHeadline: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    marginBottom: PaprikaSpacing.xs,
    textAlign: 'center',
  },
  cardBody: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
    textAlign: 'center',
  },

  sharesList: {
    alignSelf: 'stretch',
    backgroundColor: Paprika.surfaceLow,
    borderRadius: PaprikaRadius.md,
    paddingHorizontal: PaprikaSpacing.md,
    paddingVertical: PaprikaSpacing.xs,
    marginTop: PaprikaSpacing.md,
  },
  shareRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
    paddingVertical: 9,
  },
  shareRowText: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
  },

  nameCard: {
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: PaprikaRadius.lg,
    padding: PaprikaSpacing.lg,
    marginTop: PaprikaSpacing.md,
    ...PaprikaShadows.card,
  },
  nameTitle: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    marginBottom: PaprikaSpacing.xs,
    textAlign: 'center',
  },
  nameInput: {
    backgroundColor: Paprika.surfaceHighest,
    borderRadius: PaprikaRadius.md,
    paddingHorizontal: PaprikaSpacing.md,
    paddingVertical: 12,
    marginTop: PaprikaSpacing.md,
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textPrimary,
  },

  codeLabel: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textSecondary,
    textAlign: 'center',
    letterSpacing: 1.5,
    marginTop: PaprikaSpacing.xl,
    marginBottom: PaprikaSpacing.sm,
  },
  codeBox: {
    backgroundColor: Paprika.accentLight,
    borderRadius: PaprikaRadius.lg,
    paddingVertical: PaprikaSpacing.lg,
    alignItems: 'center',
  },
  codeText: {
    fontSize: 34,
    fontWeight: '600',
    letterSpacing: 8,
    color: Paprika.accent,
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
  },
  codeExpiry: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.accent,
    marginTop: PaprikaSpacing.sm,
  },

  roster: { marginTop: PaprikaSpacing.md },
  memberRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.md,
    backgroundColor: Paprika.surfaceLow,
    borderRadius: PaprikaRadius.md,
    padding: PaprikaSpacing.md,
    marginBottom: PaprikaSpacing.sm,
  },
  avatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: Paprika.accent,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarSelf: { backgroundColor: Paprika.surfaceHighest },
  avatarText: {
    ...PaprikaTypography.titleLarge,
    color: Paprika.onPrimary,
  },
  avatarTextSelf: { color: Paprika.textSecondary },
  memberText: { flex: 1, minWidth: 0 },
  memberName: {
    ...PaprikaTypography.labelLarge,
    color: Paprika.textPrimary,
  },
  memberEmail: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
    marginTop: 2,
  },
  memberTag: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textSecondary,
  },

  primaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: Paprika.accent,
    paddingVertical: 16,
    paddingHorizontal: PaprikaSpacing.xl,
    borderRadius: PaprikaRadius.xl,
    gap: PaprikaSpacing.sm,
    marginTop: PaprikaSpacing.md,
  },
  btnDisabled: { opacity: 0.6 },
  primaryBtnText: {
    ...PaprikaTypography.labelLarge,
    fontSize: 16,
    color: Paprika.onPrimary,
  },
  secondaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    paddingHorizontal: PaprikaSpacing.xl,
    borderRadius: PaprikaRadius.xl,
    borderWidth: 1,
    borderColor: Paprika.border,
    gap: PaprikaSpacing.sm,
    marginTop: PaprikaSpacing.sm,
  },
  secondaryBtnText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.textPrimary,
  },
  destructiveBtn: {
    alignItems: 'center',
    paddingVertical: PaprikaSpacing.md,
    marginTop: PaprikaSpacing.sm,
  },
  destructiveBtnText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.destructive,
  },

  // The exit (#604). Rows stacked on white with a 2px gap, so the group reads
  // as one card split in two rather than as bordered list items.
  exitActions: {
    marginTop: PaprikaSpacing.lg,
    gap: 2,
  },
  exitRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: PaprikaSpacing.sm,
    backgroundColor: Paprika.surfaceLowest,
    paddingVertical: 15,
    paddingHorizontal: PaprikaSpacing.md,
    borderRadius: PaprikaRadius.sm,
  },
  exitRowFirst: {
    borderTopLeftRadius: PaprikaRadius.lg,
    borderTopRightRadius: PaprikaRadius.lg,
  },
  exitRowLast: {
    borderBottomLeftRadius: PaprikaRadius.lg,
    borderBottomRightRadius: PaprikaRadius.lg,
  },
  exitRowTextLeave: {
    ...PaprikaTypography.labelMedium,
    fontSize: 15,
    color: Paprika.accent,
  },
  exitRowText: {
    ...PaprikaTypography.bodyMedium,
    fontSize: 15,
    color: Paprika.textSecondary,
  },

  scrim: {
    flex: 1,
    justifyContent: 'flex-end',
    backgroundColor: 'rgba(27, 28, 26, 0.32)',
  },
  sheet: {
    backgroundColor: Paprika.surfaceLowest,
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    paddingHorizontal: PaprikaSpacing.md,
    paddingTop: PaprikaSpacing.xs,
    paddingBottom: PaprikaSpacing.xl,
  },
  grabber: {
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: Paprika.surfaceHighest,
    alignSelf: 'center',
    marginTop: 4,
    marginBottom: PaprikaSpacing.md,
  },
  sheetTitle: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    marginBottom: PaprikaSpacing.xs,
  },
  sheetBody: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textSecondary,
  },
  sheetBtn: { marginTop: PaprikaSpacing.md },
  ghostBtn: {
    alignItems: 'center',
    paddingVertical: PaprikaSpacing.md,
    marginTop: PaprikaSpacing.xs,
  },
  ghostBtnText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.textSecondary,
  },

  keeps: {
    backgroundColor: Paprika.surfaceLow,
    borderRadius: PaprikaRadius.md,
    paddingVertical: 12,
    paddingHorizontal: 14,
    marginTop: PaprikaSpacing.md,
  },
  keepsHead: {
    ...PaprikaTypography.labelSmall,
    color: Paprika.textSecondary,
    letterSpacing: 0.6,
    textTransform: 'uppercase',
    marginBottom: PaprikaSpacing.xs,
  },
  keepsLine: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 3,
  },
  keepsLabel: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.textPrimary,
  },
  keepsCount: {
    fontFamily: 'Newsreader-Italic',
    fontSize: 17,
    color: Paprika.textPrimary,
  },

  bannerOk: {
    backgroundColor: Paprika.surfaceLow,
    borderRadius: PaprikaRadius.md,
    padding: 14,
    marginBottom: PaprikaSpacing.md,
  },
  bannerOkTitle: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.textPrimary,
    marginBottom: 2,
  },
  bannerOkBody: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
  },
  bannerWarn: {
    backgroundColor: Paprika.approvalNoTint,
    borderRadius: PaprikaRadius.md,
    padding: 14,
    marginBottom: PaprikaSpacing.md,
  },
  bannerWarnTitle: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.destructive,
    marginBottom: 2,
  },
  bannerWarnBody: {
    ...PaprikaTypography.bodySmall,
    color: Paprika.textSecondary,
  },
  reloadBtn: {
    alignSelf: 'flex-start',
    backgroundColor: Paprika.destructive,
    borderRadius: PaprikaRadius.md,
    paddingVertical: 9,
    paddingHorizontal: PaprikaSpacing.lg,
    marginTop: PaprikaSpacing.sm,
  },
  reloadBtnText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.onPrimary,
  },

  errorCard: {
    backgroundColor: Paprika.surfaceLowest,
    borderRadius: PaprikaRadius.lg,
    padding: PaprikaSpacing.lg,
    alignItems: 'center',
    marginTop: PaprikaSpacing.md,
  },
  errorText: {
    ...PaprikaTypography.bodyMedium,
    color: Paprika.destructive,
    textAlign: 'center',
    marginBottom: PaprikaSpacing.sm,
  },
  retryText: {
    ...PaprikaTypography.labelMedium,
    color: Paprika.accent,
  },
});
